/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ValidationErrors } from '@angular/forms';

import { SortDirection } from '@gh/core-data';

import { ColDef, GridOptions, IRowNode } from 'ag-grid-community';
import { Observable } from 'rxjs';

export type GridSortModel = {
  field: string;
  direction: SortDirection;
};

export interface CellParams {
  node: IRowNode;
  data: any;
  colDef: ColDef;
}

/**
 * Serialized view of the editable cell. This view can be stored into or calculated by state container.
 */
export interface EditableCellState {
  errors?: ValidationErrors;
  value: any;
}

/**
 * Readonly model of the cell provided to the editor in order to build view and {@link AbstractControl} model, if any.
 * Model can be calculated from the definition of the column and cell state.
 */
export interface EditableCellModel {
  required: boolean;
  errors: Maybe<ValidationErrors>;
  invalid: boolean;
  valid: boolean;
  // pristine: boolean;
  // dirty: boolean;
  editable: boolean;
  value: any;

  validate(value: any): Maybe<ValidationErrors>;
}

export interface EditableCellModelFactory {
  (params: CellParams): EditableCellModel;
}

/**
 * Model of option for cell select component
 */
export interface EditableCellOption {
  id: any;
  name: string;
}

/**
 * Factory for set of options for cell select component
 */
export interface EditableCellOptionSet {
  getOptions(params: CellParams): EditableCellOption[];

  format(params: CellParams, value: any): string;

  hasOptions?(params: CellParams): boolean;

  equalsWith?(params: CellParams, id: any, selected: any): boolean;
}

/**
 * Accessor for the editable cell.
 * It is an accessor level that responsible for integration of ag-grid editable cells with the state container.
 */
export interface EditableCellAccessor {
  name: string;

  // read-only operations
  getState(params: CellParams): EditableCellState;

  getValue(state: EditableCellState): any;

  isRequired?(params: CellParams): boolean;

  isEditable?(params: CellParams): boolean;

  validate?(params: CellParams, value: any): Maybe<ValidationErrors>;

  equals?(oldValue: any, newValue: any): boolean;

  // write-only operations
  setValue(params: CellParams, oldValue: any, newValue: any): void;
}

export interface FocusedCell {
  id: any;
  field: string;
}

/**
 * This interface serves for integration of ag-grid capabilities with state container.
 */
export interface GridModel {
  rowData: Observable<any[]>;
  editable: Observable<boolean>;

  setData(data: any): void;

  getEntityId(entity: any): any;

  getCellAccessor(name: string): EditableCellAccessor;
}

/**
 * This interface allows to manage grid state from the perspective of data.
 * It is responsibility of developer to provide implementation of this interface.
 */
export interface GridState<T> {
  rowData: Observable<T[]>;
  editable: Observable<boolean>;

  setData(data: any): void;

  getState(entity: T, name: string): EditableCellState;

  setValue(entity: T, name: string, value: any): void;

  getValue(name: string, state: EditableCellState): any;

  getEntityId(entity: T): any;

  isRequired(entity: T, name: string): boolean;

  isEditable(entity: T, name: string): boolean;

  validate(entity: T, name: string, value: any): Maybe<ValidationErrors>;

  valueEquals?(name: string, oldValue: any, newValue: any): boolean;
}

export type EditableRow<T> = T & { isDirty: boolean };

export function createGridOptions(model: GridModel): GridOptions {
  return {
    getRowId: (params) => String(model.getEntityId(params.data)),
    context: model,
  };
}
